import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Monitor } from 'lucide-react';
import { PLAY_STORE_URL, WEB_APP_URL } from '../config/urls';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  // Scroll to top function with navigation
  const handleLinkClick = (path, e) => {
    e.preventDefault();
    // Scroll to top immediately
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Navigate after a small delay to ensure scroll starts
    setTimeout(() => {
      navigate(path);
      // Scroll again after navigation to ensure it works
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }, 50);
  };

  const footerLinks = {
    product: [
      { name: 'Features', path: '/features' },
      { name: 'Pricing', path: '/pricing' },
      { name: 'Demo', path: '/demo' }
    ],
    company: [
      { name: 'Contact', path: '/contact' }
    ],
    legal: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Service', path: '/terms' }
    ]
  };


  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)
            `,
            backgroundSize: '600px 600px'
          }}
        ></div>
      </div>
      <div className="container relative z-10">
        {/* Main Footer Content */}
        <div className="grid lg:grid-cols-2 gap-16 py-16">
          {/* Brand Section */}
          <motion.div
            className="max-w-md"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Link 
              to="/" 
              onClick={(e) => handleLinkClick('/', e)} 
              className="flex items-center gap-3 text-white font-bold text-xl mb-6 transition-all duration-300 hover:scale-105"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden shadow-lg shadow-primary-500/30">
                <img 
                  src="/app_icon_512.png" 
                  alt="Wellness Pro" 
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-gradient">Wellness Pro</span>
            </Link>
            <p className="text-gray-300 leading-relaxed text-sm mb-6">
              Transform your spa business with our management solution on mobile and web.
              Streamline operations, boost revenue, and deliver exceptional customer experiences.
            </p>
            <div className="flex flex-col items-start gap-3">
              <div className="flex flex-wrap gap-3">
                <a
                  href={WEB_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-2.5 rounded-xl hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/25 transition-all duration-300 w-fit"
                >
                  <Monitor className="w-5 h-5" />
                  <span className="text-sm font-medium">Launch Web App</span>
                </a>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/40 w-fit"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="flex flex-col items-start">
                    <span className="text-[9px] leading-tight">GET IT ON</span>
                    <span className="text-sm font-bold leading-tight">Google Play</span>
                  </div>
                </a>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">30 Days Free Trial · One plan for Mobile & Web</span>
              </div>
            </div>
          </motion.div>

          {/* Links Sections */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              className="flex flex-col"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-lg font-semibold text-white mb-6">Product</h3>
              <ul className="space-y-3">
                {footerLinks.product.map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.path} 
                      onClick={(e) => handleLinkClick(link.path, e)} 
                      className="text-gray-300 text-sm hover:text-primary-500 transition-all duration-300 relative group"
                    >
                      {link.name}
                      <span className="absolute left-0 top-1/2 transform -translate-y-1/2 w-0 h-0.5 bg-primary-500 transition-all duration-300 group-hover:w-2"></span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              className="flex flex-col"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-lg font-semibold text-white mb-6">Company</h3>
              <ul className="space-y-3">
                {footerLinks.company.map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.path} 
                      onClick={(e) => handleLinkClick(link.path, e)} 
                      className="text-gray-300 text-sm hover:text-primary-500 transition-all duration-300 relative group"
                    >
                      {link.name}
                      <span className="absolute left-0 top-1/2 transform -translate-y-1/2 w-0 h-0.5 bg-primary-500 transition-all duration-300 group-hover:w-2"></span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>


            <motion.div
              className="flex flex-col"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-lg font-semibold text-white mb-6">Legal</h3>
              <ul className="space-y-3">
                {footerLinks.legal.map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.path} 
                      onClick={(e) => handleLinkClick(link.path, e)} 
                      className="text-gray-300 text-sm hover:text-primary-500 transition-all duration-300 relative group"
                    >
                      {link.name}
                      <span className="absolute left-0 top-1/2 transform -translate-y-1/2 w-0 h-0.5 bg-primary-500 transition-all duration-300 group-hover:w-2"></span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>


        {/* Contact Info */}
        <motion.div
          className="flex flex-col sm:flex-row gap-8 py-8 border-t border-white/10"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 text-gray-300 text-sm">
            <Mail className="w-4 h-4 text-primary-500 flex-shrink-0" />
            <span>support@wellnessproapp.com</span>
          </div>
          <div className="flex items-center gap-3 text-gray-300 text-sm">
            <Phone className="w-4 h-4 text-primary-500 flex-shrink-0" />
            <span>+91 9311252321</span>
          </div>
          <div className="flex items-center gap-3 text-gray-300 text-sm">
            <MapPin className="w-4 h-4 text-primary-500 flex-shrink-0" />
            <span>Noida, Uttar Pradesh, India</span>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          className="border-t border-white/10 py-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">
              © {currentYear} Wellness Pro. All rights reserved.
            </p>
            <div className="flex gap-8">
              <Link 
                to="/privacy" 
                onClick={(e) => handleLinkClick('/privacy', e)} 
                className="text-gray-400 text-sm hover:text-primary-500 transition-colors duration-300"
              >
                Privacy
              </Link>
              <Link 
                to="/terms" 
                onClick={(e) => handleLinkClick('/terms', e)} 
                className="text-gray-400 text-sm hover:text-primary-500 transition-colors duration-300"
              >
                Terms
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
